import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import {
  essHandleUnselectAllProgram,
  handleExpandSSRDetail,
  handleInstantHeatOff,
  handleInstantHeatReady,
  handleOpenMachineController,
  handleShutOff,
  handleSnowSensor,
  handleSnowSensorOff,
  selectEssSwitch,
} from 'shared/slice/essSwitchSlice';
import {
  selectTgsSwitch,
  tgsHandleInstantHeatIsReady,
  tgsHandleInstantHeatOff,
  tgsHandleOpenMachineController,
  tgsHandleShutOff,
  tgsHandleSnowSensor,
  tgsHandleSnowSensorOff,
  tgsHandleUnselectAllProgram,
} from 'shared/slice/tgsSwitchSlice';
import {
  selectTesSwitch,
  tesHandleExpandSSRDetail,
  tesHandleInstantHeatIsReady,
  tesHandleInstantHeatOff,
  tesHandleOpenMachineController,
  tesHandleShutOff,
  tesHandleSnowSensor,
  tesHandleSnowSensorOff,
  tesHandleUnselectAllProgram,
} from 'shared/slice/tesSwitchSlice';

import { selectUnits } from 'shared/slice/unitsSlice';
import { selectUserInfo, selectUserPermissions } from 'shared/slice/userSlice';
import {
  calculateTotalEnergyConsumption,
  convertCelsiusToFahrenheit,
  convertFahrenheitToCelsius,
  getConsumptionValue,
} from 'shared/helpers/helpers';
import {
  freezeBlowerDeviceService,
  freezeSwitchDeviceService,
  getDataConsumptionService,
  getTelemetryService,
  postEssCommand,
  postTesCommand,
  postTgsCommand,
} from 'shared/services';
import { handleEssGraphDate } from 'shared/slice/essSwitchSlice';
import { handleTgsGraphDate } from 'shared/slice/tgsSwitchSlice';
import { handleTesGraphDate } from 'shared/slice/tesSwitchSlice';
import {
  dataConsumptionSeriesUnit,
  getGraphTypeKey,
  toDataConsumption,
} from 'shared/helpers/helpers';
import { useGetGraphQueries, useGetSSRsQueries, useSetAndCurrentTemp } from 'shared/hooks';
import { useRealtimeGraphData } from 'shared/hooks/useRealtimeGraphData';

import SwitchDetailHeader from './SwitchDetailHeader';
import SwitchDeviceLogs from './SwitchDeviceLogs';
import SwitchHeaterStatus from './SwitchHeaterStatus';
import SwitchProgramCards from './SwitchProgramCards';
import SwitchTempDisplay from './SwitchTempDisplay';
import ThermalGauge from './ThermalGauge';
import TempSlider from './TempSlider';
import EnergyBanner from './EnergyBanner';
import MetricCircles from './MetricCircles';
import { useControlHandlers } from './ControlBox/useControlHandlers';
import { exportChartToPdf } from 'shared/components/printPDF/exportChartToPdf';



const pickGraph = (dated, live) =>
  Array.isArray(dated) && dated.length > 0 ? dated : live;


const METRIC_ICONS = {
  consumption: '/images/stats-figma/stat-energy.png',
  outdoor: '/images/stats-figma/stat-outside.png',
  setpoint: '/images/stats-figma/stat-outside.png',
  surface: '/images/stats-figma/stat-enclosure.png',
  hours: '/images/stats-figma/stat-hours.png',
};
const ratioBetween = (v, min, max) =>
  typeof v === 'number' ? Math.max(0, Math.min(1, (v - min) / (max - min))) : 0;

const SYSTEM_CONFIG = {
  ess: {
    graphType: 'ESS',
    ssrsType: 'ESS',
    hasSSRs: true,
    source: 'energy',
    unitFn: () => 'kw',
    readingUnitFn: () => 'KW/H',
    energyType: 'ess',
    systemLabel: 'ESS - ELECTRIC SWITCH SYSTEM',
    graphCategories: ['heater_graph', 'enclosure_graph', 'outside_graph'],
    consumptionCategory: 'energy',
    graphDateAction: handleEssGraphDate,
    handleShutOff: (dispatch, location, machine, deviceMac, userId) =>
      freezeSwitchDeviceService(true, deviceMac, userId)
        .then(() => dispatch(handleShutOff({ location, machine })))
        .catch((err) => console.error('fail freeze device:', err.message)),
    handleShutOn: (dispatch, location, machine, deviceMac, userId) =>
      freezeSwitchDeviceService(false, deviceMac, userId)
        .then(() => dispatch(handleShutOff({ location, machine })))
        .catch((err) => console.error('fail freeze device:', err.message)),
    handleSnowSensorOn: (dispatch, location, machine, deviceMac) => {
      postEssCommand(deviceMac, 'snow_enabled', 1);
      dispatch(handleSnowSensor({ location, machine }));
    },
    handleSnowSensorOff: (dispatch, location, machine, deviceMac) => {
      postEssCommand(deviceMac, 'snow_enabled', 0);
      dispatch(handleSnowSensorOff({ location, machine }));
    },
    handleInstantHeatOff: (dispatch, location, machine, deviceMac) => {
      postEssCommand(deviceMac, 'on_switch', 0);
      dispatch(handleInstantHeatOff({ location, machine }));
    },
    handleInstantHeatReady: (dispatch, location, machine, deviceMac, temp, isF) => {
      postEssCommand(deviceMac, 'on_switch', 1);
      postEssCommand(deviceMac, 'instant_temp', isF ? convertFahrenheitToCelsius(temp) : temp);
      dispatch(handleInstantHeatReady({ location, machine, temp, isF }));
    },
    handleOpenMachineController: (dispatch, location, machine, status) =>
      dispatch(handleOpenMachineController({ location, machine, status })),
    handleUnselectAllProgram: (dispatch, location, machine) =>
      dispatch(essHandleUnselectAllProgram({ location, machine })),
    handleExpandSSRDetail: (dispatch, location, machine, status) =>
      dispatch(handleExpandSSRDetail({ location, machine, status })),
  },
  tgs: {
    graphType: 'TGS',
    ssrsType: null,
    hasSSRs: false,
    source: 'gas',
    // the old app labels gas with superscripts in both the total box and the read
    unitFn: (isF) => (isF ? 'FT³' : 'M³'),
    readingUnitFn: (isF) => (isF ? 'FT³/H' : 'M³/H'),
    energyType: 'tgs',
    systemLabel: 'TGS - THERMAL GAS SYSTEM',
    graphCategories: ['gas_graph', 'gas_enclosure_graph', 'outside_graph'],
    consumptionCategory: 'fuel',
    graphDateAction: handleTgsGraphDate,
    handleShutOff: (dispatch, location, machine, deviceMac, userId) =>
      freezeBlowerDeviceService(true, deviceMac, userId, 'TGS')
        .then(() => dispatch(tgsHandleShutOff({ location, machine })))
        .catch((err) => console.error('fail freeze device:', err.message)),
    handleShutOn: (dispatch, location, machine, deviceMac, userId) =>
      freezeBlowerDeviceService(false, deviceMac, userId, 'TGS')
        .then(() => dispatch(tgsHandleShutOff({ location, machine })))
        .catch((err) => console.error('fail freeze device:', err.message)),
    handleSnowSensorOn: (dispatch, location, machine, deviceMac) => {
      postTgsCommand(deviceMac, 'snow_enabled', 1);
      dispatch(tgsHandleSnowSensor({ location, machine }));
    },
    handleSnowSensorOff: (dispatch, location, machine, deviceMac) => {
      postTgsCommand(deviceMac, 'snow_enabled', 0);
      dispatch(tgsHandleSnowSensorOff({ location, machine }));
    },
    handleInstantHeatOff: (dispatch, location, machine, deviceMac) => {
      postTgsCommand(deviceMac, 'on_switch', 0);
      dispatch(tgsHandleInstantHeatOff({ location, machine }));
    },
    handleInstantHeatReady: (dispatch, location, machine, deviceMac, temp, isF) => {
      postTgsCommand(deviceMac, 'instant_temp', isF ? convertFahrenheitToCelsius(temp) : temp);
      postTgsCommand(deviceMac, 'on_switch', 1);
      dispatch(tgsHandleInstantHeatIsReady({ location, machine, isF, temp }));
    },
    handleOpenMachineController: (dispatch, location, machine, status) =>
      dispatch(tgsHandleOpenMachineController({ location, machine, status })),
    handleUnselectAllProgram: (dispatch, location, machine) =>
      dispatch(tgsHandleUnselectAllProgram({ location, machine })),
    handleExpandSSRDetail: () => {},
  },
  tes: {
    graphType: 'TES',
    ssrsType: 'TES',
    hasSSRs: true,
    source: 'energy',
    unitFn: () => 'kw',
    readingUnitFn: () => 'KW/H',
    energyType: 'tes',
    systemLabel: 'TES - THERMAL ENERGY SYSTEM',
    graphCategories: ['heater_graph', 'enclosure_graph', 'outside_graph'],
    consumptionCategory: 'energy',
    graphDateAction: handleTesGraphDate,
    handleShutOff: (dispatch, location, machine, deviceMac, userId) =>
      freezeBlowerDeviceService(true, deviceMac, userId, 'TES')
        .then(() => dispatch(tesHandleShutOff({ location, machine })))
        .catch((err) => console.error('fail freeze device:', err.message)),
    handleShutOn: (dispatch, location, machine, deviceMac, userId) =>
      freezeBlowerDeviceService(false, deviceMac, userId, 'TES')
        .then(() => dispatch(tesHandleShutOff({ location, machine })))
        .catch((err) => console.error('fail freeze device:', err.message)),
    handleSnowSensorOn: (dispatch, location, machine, deviceMac) => {
      postTesCommand(deviceMac, 'snow_enabled', 1);
      dispatch(tesHandleSnowSensor({ location, machine }));
    },
    handleSnowSensorOff: (dispatch, location, machine, deviceMac) => {
      postTesCommand(deviceMac, 'snow_enabled', 0);
      dispatch(tesHandleSnowSensorOff({ location, machine }));
    },
    handleInstantHeatOff: (dispatch, location, machine, deviceMac) => {
      postTesCommand(deviceMac, 'on_switch', 0);
      dispatch(tesHandleInstantHeatOff({ location, machine }));
    },
    handleInstantHeatReady: (dispatch, location, machine, deviceMac, temp, isF) => {
      postTesCommand(deviceMac, 'instant_temp', isF ? convertFahrenheitToCelsius(temp) : temp);
      postTesCommand(deviceMac, 'on_switch', 1);
      dispatch(tesHandleInstantHeatIsReady({ location, machine, temp, isF }));
    },
    handleOpenMachineController: (dispatch, location, machine, status) =>
      dispatch(tesHandleOpenMachineController({ location, machine, status })),
    handleUnselectAllProgram: (dispatch, location, machine) =>
      dispatch(tesHandleUnselectAllProgram({ location, machine })),
    handleExpandSSRDetail: (dispatch, location, machine, status) =>
      dispatch(tesHandleExpandSSRDetail({ location, machine, status })),
  },
};

const getTemperatureLimits = (program, isF) => {
  if (program === 'optionalConstantTemp') {
    return isF ? { min: 77, max: 248 } : { min: 25, max: 120 };
  }

  return isF ? { min: 250, max: 1830 } : { min: 121, max: 999 };
};

const clampTemp = (value, min, max) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return min;
  return Math.max(min, Math.min(max, numericValue));
};


const SwitchMasterControl = ({ swtName, location, machine, isMobile, onClose }) => {
  const cfg = SYSTEM_CONFIG[swtName];
  const dispatch = useDispatch();
  const essData = useSelector(selectEssSwitch);
  const tgsData = useSelector(selectTgsSwitch);
  const tesData = useSelector(selectTesSwitch);

  const flatSwitch =
    swtName === 'ess'
      ? essData.flatEssSwitch
      : swtName === 'tgs'
      ? tgsData.flatTgsSwitch
      : tesData.flatTesSwitch;

  const switchData = flatSwitch?.[location]?.[machine] || {};

  const {
    deviceMac,
    isFaults,
    isOff,
    instantHeat = {},
    snowSensor = {},
    heatingSchedule = {},
    windFactor = {},
    isWifi,
    machineName,
    reading,
    isDisabled,
    enclosureTemp,
    outSideTemp,
    consumption,
    hoursOfUsage,
  } = switchData;

  const { isF } = useSelector(selectUnits);
  const permissions = useSelector(selectUserPermissions);
  const disabled = !permissions.WRITE || isDisabled;
  const ebpLocked = switchData.EBP_mode === 0 && switchData.isEbp;
  const {
    user: { user_id: userId },
  } = useSelector(selectUserInfo);

  const { setTemp, currentTemp } = useSetAndCurrentTemp(switchData);
  const { integratedButtonHandler: handleProgramCommand } = useControlHandlers({ swtName, location, machine });
  const [tempProgram, setTempProgram] = useState('instantHeat');
  const [tempDraft, setTempDraft] = useState(null);
  const hasConstantTempControl = swtName !== 'tgs' && Boolean(switchData.optionalConstantTemp);
  const tempProgramOptions = useMemo(() => {
    const options = [{ value: 'instantHeat', label: 'Instant Heat' }];
    if (hasConstantTempControl) {
      options.push({ value: 'optionalConstantTemp', label: 'optional Constant Temperature' });
    }
    return options;
  }, [hasConstantTempControl]);
  const tempLimits = useMemo(
    () => getTemperatureLimits(tempProgram, isF),
    [tempProgram, isF]
  );

  useEffect(() => {
    if (tempProgram === 'optionalConstantTemp' && !hasConstantTempControl) {
      setTempProgram('instantHeat');
    }
  }, [hasConstantTempControl, tempProgram]);

  useEffect(() => {
    const programTemp =
      tempProgram === 'optionalConstantTemp'
        ? switchData.optionalConstantTemp?.inputTemp
        : switchData.instantHeat?.inputTemp;
    const fallbackTemp = typeof setTemp === 'number' ? setTemp : tempLimits.min;
    const nextTemp = programTemp > 0 ? programTemp : fallbackTemp;
    setTempDraft(clampTemp(nextTemp, tempLimits.min, tempLimits.max));
  }, [
    switchData.instantHeat?.inputTemp,
    switchData.optionalConstantTemp?.inputTemp,
    setTemp,
    tempLimits.max,
    tempLimits.min,
    tempProgram,
  ]);

  useGetGraphQueries(location, machine, cfg.graphType);
  useGetSSRsQueries(location, machine, cfg.ssrsType, { enabled: cfg.hasSSRs });



  const realtimeGraph = useRealtimeGraphData(swtName, location, machine);

  const isActive = useMemo(() => {
    const programs = [instantHeat, snowSensor, heatingSchedule, windFactor];
    return programs.some((program) => program?.isActivated);
  }, [heatingSchedule, instantHeat, snowSensor, windFactor]);

  const energyConsumption = useMemo(
    () => calculateTotalEnergyConsumption(reading, cfg.energyType, isF),
    [cfg.energyType, isF, reading]
  );

  // the old detail box only reads consumption while a temperature program is
  // driving the switch — the same gate the set/current temp reads use — and
  // converts gas out of ft³ when the app is in metric
  const consumptionRead = useMemo(() => {
    const hasActiveTempProgram =
      Boolean(instantHeat?.isReady) ||
      Boolean(switchData.optionalConstantTemp?.isReady) ||
      Boolean(heatingSchedule?.isActivated) ||
      (Boolean(snowSensor?.isReady) && Boolean(snowSensor?.isActivated)) ||
      (Boolean(windFactor?.isReady) && Boolean(windFactor?.isActivated));

    if (!hasActiveTempProgram || consumption == null) return null;
    return getConsumptionValue(swtName, consumption, isF);
  }, [
    instantHeat,
    switchData.optionalConstantTemp,
    heatingSchedule,
    snowSensor,
    windFactor,
    consumption,
    swtName,
    isF,
  ]);

  const telemetryMetrics = useMemo(() => [
    // the telemetry read is per hour, unlike the running total below it
    { label: `${cfg.source} consumption`, value: consumptionRead, unit: cfg.readingUnitFn(isF), icon: METRIC_ICONS.consumption, ratio: ratioBetween(consumptionRead, 0, 50) },
    { label: 'outdoor temperature', value: outSideTemp != null ? (isF ? convertCelsiusToFahrenheit(outSideTemp) : outSideTemp) : null, unit: isF ? '°F' : '°C', icon: METRIC_ICONS.outdoor, ratio: ratioBetween(outSideTemp, -20, 60) },
    { label: 'set point', value: typeof setTemp === 'number' ? setTemp : null, unit: isF ? '°F' : '°C', icon: METRIC_ICONS.setpoint, ratio: ratioBetween(setTemp, 0, 120) },
    { label: 'surface temperature', value: enclosureTemp != null ? (isF ? convertCelsiusToFahrenheit(enclosureTemp) : enclosureTemp) : null, unit: isF ? '°F' : '°C', icon: METRIC_ICONS.surface, ratio: ratioBetween(enclosureTemp, 0, 120) },
    { label: 'hours of usage', value: hoursOfUsage, unit: 'hrs', icon: METRIC_ICONS.hours, ratio: ratioBetween(hoursOfUsage, 0, 8760) },
  ], [consumptionRead, outSideTemp, enclosureTemp, hoursOfUsage, setTemp, isF, cfg]);

  // gas arrives in ft³ and only converts to m³ when the app is in metric, so the
  // bars have to be scaled here where both the system and the unit are known
  const energyBarData = useMemo(() => {
    const points = switchData.graphDateData?.energyGasConsumptionGraphData;
    if (!Array.isArray(points)) return [];
    return points.map(([time, value]) => [time, getConsumptionValue(swtName, value, isF)]);
  }, [switchData.graphDateData?.energyGasConsumptionGraphData, swtName, isF]);

  // data consumption is stored as raw bytes, so the whole series picks one
  // unit and converts here — the same rule BarGraph applies
  const dataBarsProps = useMemo(() => {
    const points = dateGraph?.dataConsumptionGraphData;
    if (!Array.isArray(points)) return { data: [], unit: 'mb' };
    const unit = dataConsumptionSeriesUnit(points.map(([, value]) => value));
    return {
      data: points.map(([time, value]) => [time, toDataConsumption(value, unit)]),
      unit,
    };
  }, [dateGraph?.dataConsumptionGraphData]);

  if (!flatSwitch?.[location]?.[machine]) return null;

  const handleShutOffState = () => {
    if (isOff) {
      cfg.handleShutOn(dispatch, location, machine, deviceMac, userId);
    } else {
      cfg.handleShutOff(dispatch, location, machine, deviceMac, userId);
    }
  };

  const handleClose = () => {
    cfg.handleOpenMachineController(dispatch, location, machine, false);
    cfg.handleUnselectAllProgram(dispatch, location, machine);
    onClose?.();
  };

  // only the chart is captured, and scoped to this machine — several panels can
  // be mounted at once and a shared class would collect every open switch
  const reportClass = `switch-report-${[swtName, location, machine]
    .join('-')
    .replace(/[^a-zA-Z0-9_-]/g, '_')}`;

  const handleViewAndPrint = () => exportChartToPdf(reportClass);


  const dateGraph = switchData.graphDateData;

  const handleGraphSearch = ({ graphType, startDate, endDate }) => {
    if (graphType === 'realtime' || !startDate || !endDate) {
      handleGraphClear();
      return;
    }
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();

    if (!Number.isFinite(start) || !Number.isFinite(end)) {
      return;
    }

    // a search only fills the series the chosen graph draws, so anything left
    // over from the previous search would otherwise show up underneath it
    handleGraphClear();

    if (graphType === 'energy') {
      const category = cfg.consumptionCategory;
      getTelemetryService({ deviceIds: [machine], startDate: start, endDate: end, category })
        .then(({ data, unit }) => {
          const points = (data?.[0]?.data || [])
            .filter((el) => typeof el.startTime === 'number')
            .map((el) => [el.startTime, el.consumption]);
          dispatch(
            cfg.graphDateAction({ location, machine, graphType: getGraphTypeKey(category), data: points, unit })
          );
        })
        .catch((err) => console.error('graph search fail:', err?.message || err));
      return;
    }

    if (graphType === 'data') {
      const inhandId = switchData.inhand_id;
      // the modem is what reports data usage, so a switch without one has none
      if (!inhandId) return;

      getDataConsumptionService({ inhandIds: [inhandId], startDate: start, endDate: end })
        .then((res) => {
          const points = (Object.values(res)[0] || [])
            .filter((el) => el.start_date)
            // raw bytes: BarGraph picks one unit for the series and converts
            .map((el) => [el.start_date, el.total]);
          dispatch(
            cfg.graphDateAction({
              location,
              machine,
              graphType: 'dataConsumptionGraphData',
              data: points,
            })
          );
        })
        .catch((err) => console.error('graph search fail:', err?.message || err));
      return;
    }

    cfg.graphCategories.forEach((category) => {
      getTelemetryService({ deviceIds: [machine], startDate: start, endDate: end, category })
        .then(({ data, unit }) => {
          const points = (data?.[0]?.data || [])
            .filter((el) => typeof el.startTime === 'number')
            .map((el) => [el.startTime, el.avg_value]);
          dispatch(
            cfg.graphDateAction({ location, machine, graphType: getGraphTypeKey(category), data: points, unit })
          );
        })
        .catch((err) => console.error('graph search fail:', err?.message || err));
    });
  };

  const handleGraphClear = () => {
    [
      ...cfg.graphCategories.map(getGraphTypeKey),
      getGraphTypeKey(cfg.consumptionCategory),
      'dataConsumptionGraphData',
    ].forEach((graphType) => {
      dispatch(cfg.graphDateAction({ location, machine, graphType, data: [] }));
    });
  };

  const handleTempApply = (value) => {
    const nextTemp = clampTemp(value, tempLimits.min, tempLimits.max);
    setTempDraft(nextTemp);
    if (tempProgram === 'optionalConstantTemp') {
      handleProgramCommand('constantTemp', 'on', nextTemp);
      return;
    }

    handleProgramCommand('instantHeat', 'on', nextTemp);
  };

  const heaterTemperatureGraph = swtName === 'tgs' ? realtimeGraph.gasGraphData : realtimeGraph.heaterGraphData;
  const datedHeaterTemperatureGraph = swtName === 'tgs' ? dateGraph?.gasGraphData : dateGraph?.heaterGraphData;

  const statusControls = (
    <PowerRow>
      <PowerBtn $isOff={isOff} onClick={handleShutOffState} disabled={disabled}>
        <PowerIcon viewBox="0 0 16 16" fill="none">
          <path
            d="M8 1v6M4.22 3.22A6 6 0 1 0 11.78 3.22"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </PowerIcon>
        <span>{isOff ? 'TURN ON' : 'SHUT OFF'}</span>
      </PowerBtn>

      <StatusChips>
        <Chip $active={isWifi}>
          <ChipIcon src={isWifi ? '/images/wifi-active.svg' : '/images/wifi-none.svg'} />
          <span>WiFi</span>
        </Chip>
        <Chip $active={!isFaults}>
          <ChipIcon src={isFaults ? '/images/fault-sm-active.svg' : '/images/fault-sm.svg'} />
          <span>Faults</span>
        </Chip>
      </StatusChips>

      <EnergyCard>
        <EnergyLabel>{cfg.source} consumption</EnergyLabel>
        <EnergyValue>
          {energyConsumption}
          <EnergyUnit>{cfg.unitFn(isF)}</EnergyUnit>
        </EnergyValue>
      </EnergyCard>
    </PowerRow>
  );

  const telemetryColumn = (
    <>
      {statusControls}

      <SwitchTempDisplay
        series={[
          { key: 'ht', color: '#F97316', data: pickGraph(datedHeaterTemperatureGraph, heaterTemperatureGraph), enabled: Boolean(switchData.thermocouple) },
          { key: 'et', color: '#00D492', data: pickGraph(dateGraph?.enclosureGraphData, realtimeGraph.enclosureGraphData) },
          { key: 'ot', color: '#2B95D2', data: pickGraph(dateGraph?.outsideGraphData, realtimeGraph.outsideGraphData) },
        ]}
        setPoint={typeof setTemp === 'number' ? setTemp : null}
        currentTemp={typeof currentTemp === 'number' ? currentTemp : null}
        isF={isF}
        systemLabel={cfg.systemLabel}
        realTime={!dateGraph?.intervalUnit}
        intervalUnit={dateGraph?.intervalUnit}
        source={cfg.source}
        energyBars={{ data: energyBarData, unit: cfg.unitFn(isF) }}
        dataBars={dataBarsProps}
        reportClass={reportClass}
        onSearch={handleGraphSearch}
        onClear={handleGraphClear}
      />

      <GaugeSection>
        <GaugeSectionTitle>Thermal Process Controller</GaugeSectionTitle>
        <ThermalGauge currentTemp={currentTemp} setTemp={setTemp} isF={isF} />
      </GaugeSection>

      <TempSlider
        value={tempDraft ?? tempLimits.min}
        onChange={setTempDraft}
        onApply={handleTempApply}
        min={tempLimits.min}
        max={tempLimits.max}
        unit={isF ? 'F' : 'C'}
        programOptions={tempProgramOptions}
        selectedProgram={tempProgram}
        onProgramChange={setTempProgram}
        applyLabel={tempProgram === 'optionalConstantTemp' ? 'APPLY OPTIONAL CONSTANT Temperature' : 'APPLY INSTANT HEAT'}
        disabled={!permissions.WRITE || isOff || isDisabled || ebpLocked}
      />

      <EnergyBanner value={energyConsumption} unit={cfg.unitFn(isF)} source={cfg.source} />

      <MetricCircles metrics={telemetryMetrics} />

      {cfg.hasSSRs && (
        <SwitchHeaterStatus
          swtName={swtName}
          location={location}
          machine={machine}
          disabled={isOff || disabled}
        />
      )}
      <LogsWrapper>
        <SwitchDeviceLogs />
      </LogsWrapper>
    </>
  );

  return (
    <Wrapper>
      <SwitchDetailHeader
        machineName={machineName}
        isOff={isOff}
        isFaults={isFaults}
        isActive={isActive}
        onClose={handleClose}
        onViewAndPrint={handleViewAndPrint}
      />

      <ScrollArea>
        {isMobile ? (
          <MobileStack>
            {telemetryColumn}
            <SwitchProgramCards swtName={swtName} location={location} machine={machine} />
          </MobileStack>
        ) : (
          <DesktopLayout>
            <TelemetryColumn>{telemetryColumn}</TelemetryColumn>
            <ProgramsColumn>
              <SwitchProgramCards swtName={swtName} location={location} machine={machine} />
            </ProgramsColumn>
          </DesktopLayout>
        )}
      </ScrollArea>
    </Wrapper>
  );
};

export default SwitchMasterControl;
const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  background: var(--switch-shell-bg);
  border: 1px solid rgba(29, 41, 61, 0.72);
  border-radius: 20px;
`;

const ScrollArea = styled.div`
  flex: 1;
  padding-bottom: 20px;
`;

const MobileStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DesktopLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 348px;
  gap: 20px;
  padding: 0 20px 20px 0;
  align-items: start;

  @media (max-width: 1180px) {
    grid-template-columns: 1fr;
    padding-right: 0;
  }
`;

const TelemetryColumn = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ProgramsColumn = styled.aside`
  position: sticky;
  top: 16px;
  align-self: start;

  @media (max-width: 1180px) {
    position: static;
  }
`;

const PowerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px 0;
  flex-wrap: wrap;
`;

const PowerBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 18px;
  border-radius: 12px;
  border: 0.8px solid rgba(29, 41, 61, 0.5);
  cursor: pointer;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.8px;
  transition: all 0.2s;

  ${({ $isOff }) =>
    $isOff
      ? css`
          background: rgba(0, 188, 125, 0.1);
          color: #00d492;
          border-color: rgba(0, 212, 146, 0.3);

          &:hover {
            background: rgba(0, 188, 125, 0.2);
          }
        `
      : css`
          background: rgba(238, 75, 86, 0.1);
          color: #ee4b56;
          border-color: rgba(238, 75, 86, 0.3);

          &:hover {
            background: rgba(238, 75, 86, 0.2);
          }
        `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PowerIcon = styled.svg`
  width: 16px;
  height: 16px;
`;

const StatusChips = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Chip = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 10px;
  border: 0.8px solid rgba(29, 41, 61, 0.5);
  background: var(--switch-panel-bg);
  font-size: 10px;
  color: ${({ $active }) => ($active ? 'var(--switch-text-muted)' : 'var(--switch-text-faint)')};
`;

const ChipIcon = styled.img`
  width: 14px;
  height: 14px;
`;

const EnergyCard = styled.div`
  margin-left: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  padding: 8px 16px;
  border-radius: 12px;
  border: 0.8px solid rgba(29, 41, 61, 0.5);
  background: var(--switch-panel-bg);

  @media (max-width: 900px) {
    margin-left: 0;
  }
`;

const EnergyLabel = styled.span`
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #62748e;
`;

const EnergyValue = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
`;

const EnergyUnit = styled.span`
  font-size: 10px;
  font-weight: 500;
  color: #62748e;
  margin-left: 4px;
`;




const LogsWrapper = styled.div``;

const GaugeSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 20px;
`;

const GaugeSectionTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.3px;
`;
